import { FilterParam } from './filter-param';
import { FilterOperator } from './filter-operator';
import { keys, isNil, unionWith, isEqual } from 'lodash';
import { Moment, isMoment } from 'moment';
import { FilterDataType } from './filter-data-type';

import { CommonHelper } from '../../helpers';

export class FilterObjectBuilder {
    private filterParams: FilterParam[];

    private _standardDateFormat = 'YYYYMMDDHHmmss';

    public set standardDateFormat(value: string) {
        this._standardDateFormat = value;
    }

    private _configs: FilterObjectConfig[] = [];
    constructor(configs?: Array<FilterObjectConfig>) {
        this.filterParams = [];
        if (!isNil(configs)) {
            this._configs.push(...configs);
        }
    }

    get config(): Array<FilterObjectConfig> {
        return this._configs;
    }

    set config(configs: Array<FilterObjectConfig>) {
        this._configs.push(...configs);
    }

    hasParam(): boolean {
        return this.filterParams.length !== 0;
    }

    by(field: string, operator: FilterOperator, value: any): FilterObjectBuilder {
        if (
            !isNil(value) &&
            value !== '' &&
            !this.filterParams.some((x) => {
                x.isEqual(field, operator, value);
            })
        ) {
            switch (operator) {
                case FilterOperator.like:
                    value = `%${value.trim()}%`;
                    break;
                default:
                    break;
            }

            const isTrimNeeded = typeof value === 'string';
            this.filterParams.push(new FilterParam(field, operator, isTrimNeeded ? value?.trim() : value));
        }

        return this;
    }
    byObject(object: any, operator: FilterOperator): FilterObjectBuilder {
        let replacedKey: string;
        let multiSelectArray = '';
        const paramObject = CommonHelper.flattenObject(object);

        // create a default config
        keys(paramObject)
            .filter((key: string) => !isNil(paramObject[key]) && paramObject[key] !== '')
            .forEach((key: string) => {
                const config = this._configs.find((cf: FilterObjectConfig) => cf.field === key);
                if (!isNil(config)) {
                    switch (config.dataType) {
                        case FilterDataType.dateRange:
                            const dateValRang = paramObject[key] as Moment;
                            if (config.operator === FilterOperator.gte) {
                                const fromRange = dateValRang.hour(0).minute(0).second(0).clone();
                                this.by(
                                    key.split('_from')[0],
                                    config.operator,
                                    fromRange.format(this._standardDateFormat)
                                );
                            }

                            if (config.operator === FilterOperator.lte) {
                                const toRange = dateValRang.hour(23).minute(59).second(59).clone();
                                this.by(key.split('_to')[0], config.operator, toRange.format(this._standardDateFormat));
                            }

                            break;

                        case FilterDataType.dateEqual:
                            const dateEqualVal = paramObject[key] as Moment;
                            const from = dateEqualVal.hour(0).minute(0).second(0).clone();
                            const to = dateEqualVal.hour(23).minute(59).second(59).clone();
                            this.by(key, FilterOperator.gte, from.format(this._standardDateFormat));
                            this.by(key, FilterOperator.lte, to.format(this._standardDateFormat));
                            break;
                        case FilterDataType.date:
                            const dateVal = paramObject[key] as Moment;
                            const dateFormattedValue = dateVal.hour(0).minute(0).second(0).clone();
                            this.by(key, config.operator, dateFormattedValue.format(this._standardDateFormat));
                            break;
                        default:
                            this.by(key, config.operator, paramObject[key]);
                            break;
                    }
                } else {
                    if (isMoment(paramObject[key])) {
                        const dateVal = paramObject[key] as Moment;
                        const from = dateVal.hour(0).minute(0).second(0).clone();
                        const to = dateVal.hour(23).minute(59).second(59).clone();
                        this.by(key, FilterOperator.gte, from.format(this._standardDateFormat));
                        this.by(key, FilterOperator.lte, to.format(this._standardDateFormat));
                    } else {
                        if (key.match(/\.\d+/g)) {
                            replacedKey = key.replace(/\.\d+/g, '');
                            multiSelectArray += `${paramObject[key]},`;
                        } else {
                            this.by(key, operator, paramObject[key]);
                        }
                    }
                }
            });
        if (!isNil(replacedKey) && replacedKey !== '') {
            const replacedValue = multiSelectArray.replace(/\,$/, '');
            this.by(replacedKey, FilterOperator.in, replacedValue);
        }
        return this;
    }

    reset(): FilterObjectBuilder {
        this.filterParams.length = 0;
        return this;
    }

    /**
     * @deprecated
     */
    filterString(): string {
        const queryParams = this.filterParams.map((param: FilterParam) => param.toString()).join('&');
        return `?filters=${queryParams}`;
    }

    filterParamString(): string {
        return unionWith(this.filterParams, isEqual)
            .map((param: FilterParam) => param.toString())
            .join('&');
    }

    apiFilter(): any {
        return {
            filters: this.hasParam() ? this.filterParamString() : undefined,
        };
    }
}

export class FilterObjectConfig {
    constructor(
        public field: string,
        public dataType: FilterDataType = FilterDataType.text,
        public operator: FilterOperator = FilterOperator.eq
    ) {}
}
